import {
    useEffect,
    useState,
    useRef,
    useContext,
    useCallback
} from 'preact/hooks';
import classNames from 'classnames';
import { Transition } from 'react-transition-group';

import PollOption from './PollOption/pollOption.component';
import styleSheet from './poll.module.scss';
import { PollContext } from '../../potg/potg.component';
import ArrowIcon from '../shared-components/icons/ArrowIcon';

const styles = styleSheet.locals || {};

/**
 * Component for showing POTG poll options
 *
 * @returns {Function} <PollOptionsList />
 */
function PollOptionsList() {
    const {
        poll,
        selectedOptions,
        setSelectedOptions,
        pollOpen,
        pollUserAnswer = null,
        startEmbedAnimation,
        isSignedIn,
        hasOptionImages,
        isGated
    } = useContext(PollContext);

    const question = poll.questions[0];

    const showingResults =
        isGated && !isSignedIn
            ? false
            : (!pollOpen || pollUserAnswer) &&
              poll.questions[0]?.options.length > 0;

    const nodeRef = useRef(null);

    let optionsToRender = question.options;

    const duration = 300;
    const transitionStyles = { entered: { opacity: 1 } };
    const transitionDelay = 100;
    const transitionLength = 300;
    const optionsVisibleDelay =
        optionsToRender.length * transitionDelay + transitionLength;

    const wrapperRef = useRef(null);

    // Use a local state for options so we can rotate them
    const options = [...optionsToRender];

    const offset = options.length;
    // eslint-disable-next-line no-magic-numbers
    const [virtualIndex, setVirtualIndex] = useState(offset * 3); // start at middle set
    const [transition, setTransition] = useState(true);
    const [infiniteOptions, setInfiniteOptions] = useState([
        ...options,
        ...options,
        ...options,
        ...options,
        ...options
    ]);
    const [forceIndexUpdate, setForceIndexUpdate] = useState(0);

    // Center the current item in the wrapper, accounting for scale
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        let resizeTimeout = null;

        /**
         * Function to center the currently selected item in the scrollable wrapper
         */
        function centerCurrentItem() {
            const wrapper = wrapperRef.current;
            if (!wrapper) {
                return;
            }

            const currentItem = wrapper.firstChild.children[virtualIndex];
            if (!currentItem) {
                return;
            }

            // Get the actual rendered dimensions
            const wrapperRect = wrapper.getBoundingClientRect();
            const itemRect = currentItem.getBoundingClientRect();

            // Calculate scale by comparing offset dimensions with bounding rect
            const scaleX = wrapperRect.width / wrapper.offsetWidth;

            // Adjust the centering calculation to account for scale
            const wrapperScrollLeft = wrapper.scrollLeft;
            const scaledItemWidth = itemRect.width / scaleX;
            const scaledOffset = (itemRect.left - wrapperRect.left) / scaleX;

            const itemCenter =
                scaledOffset + wrapperScrollLeft + scaledItemWidth / 2;
            const wrapperCenter = wrapper.offsetWidth / 2;

            wrapper.style.transition = transition ? 'scroll-left 0.3s' : 'none';
            wrapper.scrollLeft = itemCenter - wrapperCenter;

            if (
                !selectedOptions?.[0] ||
                selectedOptions[0] !== infiniteOptions[virtualIndex]
            ) {
                setSelectedOptions([infiniteOptions[virtualIndex]]);
            }
        }

        centerCurrentItem();

        // Debounced version to prevent multiple rapid calls
        /**
         *
         */
        function debouncedCenterItem() {
            clearTimeout(resizeTimeout);
            // eslint-disable-next-line no-magic-numbers
            resizeTimeout = setTimeout(centerCurrentItem, 100);
        }

        // Create resize observer
        const resizeObserver = new ResizeObserver(debouncedCenterItem);

        // Observe both window and wrapper
        resizeObserver.observe(wrapper);
        window.addEventListener('resize', debouncedCenterItem);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', debouncedCenterItem);
            clearTimeout(resizeTimeout);
        };
    }, [
        virtualIndex,
        infiniteOptions,
        transition,
        selectedOptions,
        setSelectedOptions,
        forceIndexUpdate
    ]);

    // Handle infinite scroll effect (jump to middle third when at edge)
    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }
        const handleTransitionEnd = () => {
            if (virtualIndex >= infiniteOptions.length - options.length) {
                setTransition(false);
                setInfiniteOptions([...infiniteOptions, ...options]);
            }
            if (virtualIndex < options.length) {
                setTransition(false);
                setInfiniteOptions([...options, ...infiniteOptions]);
                setVirtualIndex(virtualIndex + options.length); // shift index right by one set
            }
        };
        wrapper.addEventListener('transitionend', handleTransitionEnd);
        return () =>
            wrapper.removeEventListener('transitionend', handleTransitionEnd);
    }, [virtualIndex, offset]);

    // Re-enable transition after jump
    useEffect(() => {
        if (!transition) {
            setTimeout(() => setTransition(true), 0);
        }
    }, [transition]);

    const MOMENTUM_DURATION = 200; // Duration of momentum scrolling in ms

    /**
     * Handles the start of a touch event
     *
     * @param {TouchEvent} event - The touch event object
     * @returns {object | null} Touch start data containing x, y coordinates and timestamp
     */
    const handleTouchStart = useCallback((event) => {
        if (event.touches?.length === 1) {
            return {
                x: event.touches[0].clientX,
                y: event.touches[0].clientY,
                scrollLeft: wrapperRef.current.scrollLeft,
                time: Date.now()
            };
        }
        return null;
    }, []);

    /**
     * Handles touch movement and determines if it's a horizontal swipe
     *
     * @param {TouchEvent} event - The touch event object
     * @param {object} touchStart - The initial touch coordinates
     * @param {Function} setIsHorizontal - Callback to update horizontal swipe state
     * @param {boolean} currentIsHorizontal - Current state of horizontal swipe
     */
    const handleTouchMove = useCallback((event, touchStart) => {
        if (!touchStart) {
            return;
        }

        const wrapper = wrapperRef.current;
        const touch = event.touches[0];
        const deltaX = touchStart.x - touch.clientX;

        wrapper.style.scrollBehavior = 'auto';
        wrapper.scrollLeft = touchStart.scrollLeft + deltaX;
    }, []);

    /**
     * Finds the closest item to the center of the viewport based on scroll position
     *
     * @param {number} scrollLeft
     * @returns
     */
    const findClosestItem = (scrollLeft) => {
        const wrapper = wrapperRef.current;
        const itemWidth = wrapper.firstChild.children[0].offsetWidth;
        const wrapperCenter = wrapper.offsetWidth / 2;
        const viewportCenter = scrollLeft + wrapperCenter;

        // Get the base index of the item closest to the center
        const baseIndex = Math.floor(viewportCenter / itemWidth);

        // Calculate centers for current, previous and next items
        const itemPositions = [-1, 0, 1].map((offset) => {
            const index = baseIndex + offset;
            const itemCenter = index * itemWidth + itemWidth / 2;
            const distanceToCenter = Math.abs(viewportCenter - itemCenter);
            return { index, distance: distanceToCenter };
        });

        // Find the item with minimum distance to center
        const closestItem = itemPositions.reduce((closest, current) => {
            return current.distance < closest.distance ? current : closest;
        }, itemPositions[1]); // Start with center item as default

        return {
            index: closestItem.index,
            scrollLeft:
                closestItem.index * itemWidth - wrapperCenter + itemWidth / 2
        };
    };

    /**
     * Handles the end of a touch event
     *
     * @param {TouchEvent} event - The touch event object
     * @param {object} touchStart - The initial touch data
     * @param {boolean} isHorizontalSwipe - Whether the swipe was horizontal
     */
    const handleTouchEnd = useCallback(
        (event, touchStart) => {
            if (!touchStart) {
                return;
            }

            const wrapper = wrapperRef.current;
            const touch = event.changedTouches[0];
            const deltaX = touchStart.x - touch.clientX;
            const deltaTime = Date.now() - touchStart.time;
            const velocity = deltaX / deltaTime;

            // Calculate target scroll position based on velocity
            const momentumDistance = velocity * MOMENTUM_DURATION;
            const targetScrollLeft = wrapper.scrollLeft + momentumDistance;

            // Always find and snap to closest item
            const { index } = findClosestItem(targetScrollLeft);

            wrapper.style.scrollBehavior = 'smooth';

            // If the closest item is already centered, force snap to it
            if (virtualIndex === index) {
                setForceIndexUpdate(forceIndexUpdate + 1);
            }

            setVirtualIndex(index);
        },
        [setVirtualIndex, forceIndexUpdate, virtualIndex]
    );

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) {
            return;
        }

        let touchStart = null;

        const onTouchStart = (event) => {
            event.preventDefault();
            touchStart = handleTouchStart(event);
        };

        const onTouchMove = (event) => {
            event.preventDefault();
            handleTouchMove(event, touchStart);
        };

        const onTouchEnd = (event) => {
            handleTouchEnd(event, touchStart);
            touchStart = null;
        };

        wrapper.addEventListener('touchstart', onTouchStart, {
            passive: false
        });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd);

        return () => {
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

    const moveLeft = () => {
        setTransition(true);
        setVirtualIndex((prev) => prev - 1);
    };
    const moveRight = () => {
        setTransition(true);
        setVirtualIndex((prev) => prev + 1);
    };

    const handleOptionClick = (index) => {
        // Don't do anything if clicking the already centered item
        if (index === virtualIndex) {
            return;
        }

        setTransition(true);
        setVirtualIndex(index);
    };

    return (
        <div part="potg-options" className={styles.pollOptionsWrapper}>
            <button
                onClick={moveLeft}
                className={classNames(
                    styles.pollOptionsMoveButton,
                    styles.pollOptionsMoveButtonLeft,
                    {
                        [styles.pollOptionsMoveButtonDisabled]:
                            virtualIndex === options.length
                    }
                )}
                name="scroll-left"
                disabled={virtualIndex === options.length}
            >
                <ArrowIcon className={styles.pollOptionsArrowIconLeft} />
            </button>
            <button
                onClick={moveRight}
                className={classNames(
                    styles.pollOptionsMoveButton,
                    styles.pollOptionsMoveButtonRight
                )}
                name="scroll-right"
            >
                <ArrowIcon className={styles.pollOptionsArrowIconRight} />
            </button>
            <div className={styles.pollOptionsViewport} ref={wrapperRef}>
                <ul className={styles.pollOptions}>
                    {infiniteOptions.map((option, index) => {
                        const isSelected = selectedOptions?.find(
                            (opt) => opt.text === option.text
                        );
                        const isAnswered =
                            (Array.isArray(pollUserAnswer) &&
                                pollUserAnswer.includes(option.option_id)) ||
                            (typeof pollUserAnswer === 'string' &&
                                pollUserAnswer.includes(option.option_id));
                        const delay =
                            transitionDelay * ((index % options.length) + 1);

                        return (
                            <Transition
                                nodeRef={nodeRef}
                                timeout={duration}
                                in={startEmbedAnimation}
                                key={`${option.text}-${index}`}
                            >
                                {(state) => (
                                    <li
                                        className={classNames(
                                            styles.pollOptionItem,
                                            {
                                                [styles.pollOptionItemCenter]:
                                                    index === virtualIndex,
                                                [styles.pollOptionItemNoImage]:
                                                    !hasOptionImages
                                            }
                                        )}
                                        style={{
                                            ...transitionStyles[state],
                                            transitionDelay: `${delay}ms`
                                        }}
                                        onClick={() => handleOptionClick(index)}
                                        role="button"
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault();
                                                handleOptionClick(index);
                                            }
                                        }}
                                    >
                                        <PollOption
                                            option={option}
                                            isSelected={isSelected}
                                            isAnswered={isAnswered}
                                            showingResults={showingResults}
                                            transitionStyles={transitionStyles}
                                            transitionState={state}
                                            optionsVisibleDelay={
                                                optionsVisibleDelay
                                            }
                                            index={index}
                                        />
                                    </li>
                                )}
                            </Transition>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}

export default PollOptionsList;
